import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { useSelector, shallowEqual } from "react-redux";
import { useMemo, useCallback } from "react";
import { User } from "@src/types/user";
import {
  PriceModes,
  PurchasingAgentTypes,
} from "@components/molecules/PriceModeCard/types";
import { ProductCategory } from "@utils/constants";
import { RootState } from "@redux/store";
import { JBTDetails } from "@src/types/DiamondDetail";
import { useTheme } from "styled-components/native";
import {
  INITIAL_SEARCH_RESULT_VIEW_TYPE,
  SEARCH_RESULT_VIEW_TYPE,
  SearchResultsAndFavoritesEnum,
} from "@utils/product/searchResultConstants";

export const DEFAULT_USER_INFO: Partial<User> = {};
const DEFAULT_LAST_AD_SEQUENCE = 0;
const initialState: {
  user: typeof DEFAULT_USER_INFO;
  searchResultViewType: typeof SEARCH_RESULT_VIEW_TYPE;
  searchResultViewTypeInitial: typeof INITIAL_SEARCH_RESULT_VIEW_TYPE;
  isGuest: boolean;
  customLogoURL: string | null;
  hideCustomLogo: boolean;
  lastAdSequesnce: number;
  columnWidths: {
    [productType: string]: {
      [priceMode: number]: {
        [key: string]: number;
      };
    };
  };
  companyLocations: [];
  popUpVisibilty: {
    visible: boolean;
    currentTimeStamp: number | null;
  };
} = {
  user: DEFAULT_USER_INFO,
  searchResultViewType: SEARCH_RESULT_VIEW_TYPE,
  searchResultViewTypeInitial: INITIAL_SEARCH_RESULT_VIEW_TYPE,
  isGuest: false,
  customLogoURL: null,
  hideCustomLogo: false,
  lastAdSequesnce: 0,
  columnWidths: {},
  companyLocations: [],
  popUpVisibilty: {
    visible: true,
    currentTimeStamp: null,
  },
};

export const userInfoSlice = createSlice({
  name: "userInfo",
  initialState,
  reducers: {
    setUserInfo: (state, action) => {
      state.user = action.payload;
    },
    updateUserInfo: (state, action) => {
      const { key, value } = action.payload;
      if (state.user) {
        state.user[key] = value;
      }
    },
    clearUserInfo: (state) => {
      state.user = DEFAULT_USER_INFO;
      state.isGuest = false;
      state.searchResultViewType = SEARCH_RESULT_VIEW_TYPE;
      state.searchResultViewTypeInitial = INITIAL_SEARCH_RESULT_VIEW_TYPE;
      state.lastAdSequesnce = DEFAULT_LAST_AD_SEQUENCE;
      state.columnWidths = {};
      state.popUpVisibilty = {
        visible: true,
        currentTimeStamp: null,
      };
    },
    setCustomLogoURL: (state, action) => {
      state.customLogoURL = action.payload;
    },
    setHideCustomLogo: (state, action) => {
      state.hideCustomLogo = action.payload;
    },
    setIsGuest: (state, action) => {
      state.isGuest = action.payload;
    },
    setSearchResultViewType: (
      state,
      action: PayloadAction<{
        category: ProductCategory;
        viewType: SearchResultsAndFavoritesEnum;
      }>
    ) => {
      if (state.searchResultViewType === undefined) {
        state.searchResultViewType = SEARCH_RESULT_VIEW_TYPE;
      }
      const { category, viewType } = action.payload;
      if (state.searchResultViewType[category] !== undefined) {
        state.searchResultViewType[category] = viewType;
      }
    },
    clearSearchResultViewType: (state) => {
      state.searchResultViewType = SEARCH_RESULT_VIEW_TYPE;
    },
    setSearchResultViewTypeInitial: (
      state,
      action: PayloadAction<{
        category: ProductCategory;
        initial: boolean;
      }>
    ) => {
      const { category, initial } = action.payload;
      if (state.searchResultViewTypeInitial[category] !== undefined) {
        state.searchResultViewTypeInitial[category] = initial;
      }
    },
    setLastAdSequence: (state, action) => {
      state.lastAdSequesnce = action.payload;
    },
    setColumnWidths: (
      state,
      action: PayloadAction<{
        productType: string;
        priceMode: number;
        key: string;
        width: number;
      }>
    ) => {
      const { productType, priceMode, key, width } = action.payload;
      if (!state.columnWidths[productType]) {
        state.columnWidths[productType] = {};
      }
      if (!state.columnWidths[productType][priceMode]) {
        state.columnWidths[productType][priceMode] = {};
      }

      state.columnWidths[productType][priceMode][key] = Math.max(30, width);
    },
    setMultipleColumnWidths: (
      state,
      action: PayloadAction<{
        productType: string;
        priceMode: number;
        widths: { [key: string]: number };
      }>
    ) => {
      const { productType, priceMode, widths } = action.payload;
      if (!state.columnWidths[productType]) {
        state.columnWidths[productType] = {};
      }
      if (!state.columnWidths[productType][priceMode]) {
        state.columnWidths[productType][priceMode] = {};
      }

      Object.entries(widths).forEach(([key, width]) => {
        state.columnWidths[productType][priceMode][key] = Math.max(30, width);
      });
    },
    resetColumnWidths: (
      state,
      action: PayloadAction<{ productType: string; priceMode: string }>
    ) => {
      const { productType, priceMode } = action.payload;
      if (state.columnWidths[productType]) {
        state.columnWidths[productType][priceMode] = {};
      }
    },
    setCompanyLocations: (state, action) => {
      state.companyLocations = action.payload;
    },
    setPopUpVisibilty: (
      state,
      action: PayloadAction<{
        visible?: boolean;
        currentTimeStamp?: number | null;
      }>
    ) => {
      state.popUpVisibilty = {
        ...state.popUpVisibilty,
        ...action.payload,
      };
    },
  },
});

export const {
  setUserInfo,
  updateUserInfo,
  clearUserInfo,
  setIsGuest,
  setSearchResultViewType,
  clearSearchResultViewType,
  setSearchResultViewTypeInitial,
  setCustomLogoURL,
  setHideCustomLogo,
  setLastAdSequence,
  setColumnWidths,
  setMultipleColumnWidths,
  resetColumnWidths,
  setCompanyLocations,
  setPopUpVisibilty,
} = userInfoSlice.actions;

export const selectViewTypeForCategory = (
  state: RootState,
  category: ProductCategory
): SearchResultsAndFavoritesEnum => {
  if (
    !state.userInfo ||
    !state.userInfo.searchResultViewType ||
    category === ProductCategory.JEWELRY ||
    category === ProductCategory.LABGROWN_JEWELRY
  ) {
    return SearchResultsAndFavoritesEnum.GRID;
  }

  return (
    state.userInfo.searchResultViewType[category] ||
    SearchResultsAndFavoritesEnum.GRID
  );
};

// ============================================
// MEMOIZED SELECTORS - Prevents unnecessary re-renders
// ============================================

// Base selectors
const selectUserInfoState = (state: RootState) => state.userInfo;

export const selectUserIframeStatus = (state: RootState) =>
  state.userInfo?.user?.iframe_status;

export const selectUserId = (state: RootState) => state.userInfo?.user?.id;

export const selectUserPriceMode = (state: RootState) =>
  state.userInfo?.user?.price_mode;

export const selectUserIsPriceModeLock = (state: RootState) =>
  state.userInfo?.user?.is_price_mode_lock;

// Memoized selector that combines all userInfo data into a single subscription
const selectUserInfoData = createSelector(
  [selectUserInfoState],
  (userInfo) => ({
    user: userInfo.user as User,
    isGuest: userInfo.isGuest,
    lastAdSequesnce: userInfo.lastAdSequesnce,
    popUpVisibilty: userInfo.popUpVisibilty,
    customLogoURL: userInfo.customLogoURL,
    hideCustomLogo: userInfo.hideCustomLogo,
  })
);

// Memoized selector for feature settings (frequently accessed)
export const selectFeatureSettings = createSelector(
  [selectUserInfoState],
  (userInfo) => userInfo.user?.feature_settings
);

// Memoized selector for plan details
export const selectPlanDetails = createSelector(
  [selectUserInfoState],
  (userInfo) => userInfo.user?.plan_details
);

// ============================================
// OPTIMIZED useUserInfo HOOK
// ============================================

export const useUserInfo = () => {
  // Single memoized selector instead of 6 separate useSelector calls
  const {
    user,
    isGuest,
    lastAdSequesnce,
    popUpVisibilty,
    customLogoURL,
    hideCustomLogo,
  } = useSelector(selectUserInfoData, shallowEqual);

  const theme = useTheme();

  // Memoized simple getters - only recreated when user changes
  const getters = useMemo(
    () => ({
      getRole: () => user?.role,
      firstSearch: () => user?.first_search,
      hasTermsAccepted: () => user?.accepted_terms,
      getToken: () => user?.token,
      getUserId: () => user?.id,
      getPriceMode: () => user?.price_mode,
      getProductMarkups: () => user?.product_markups,
      getMarkupAmount: () => user?.markup_amount,
      getMaxSearchKeyValues: () => user?.max_search_key_values,
      getPDPCustomisation: () => user?.pdps,
      getNavigationCustomisation: () => user?.json_navigation_menus,
      getAllowedStockTypes: () => user?.allowed_stock_items_type,
      getLabGrownText: () => user?.lab_grown_pop_text,
      getUserPriceMode: () => user?.price_mode,
      getUserMarkupAmount: () => user?.markup_amount,
      getUserName: () => user?.name,
      getVdbSetting: () => user?.vdb_setting,
      getFeatureSettings: () => user?.feature_settings,
      getSeletedViewType: () => user?.search_view_switch,
      companyName: () => user?.company,
      jbtDetails: () => user?.jbt_detail as JBTDetails,
      canRequest: () => user?.can_request,
      lockCurrency: () => user?.lock_currency,
      isIFrame: () => user?.iframe_status,
      getCheckoutMethod: () => user?.feature_settings?.checkout_method,
    }),
    [user]
  );

  // Memoized boolean checks - only recreated when user changes
  const booleanChecks = useMemo(
    () => ({
      isPAUser: () =>
        user?.price_mode === PriceModes.ORANGE &&
        user?.bmh_request_type === PurchasingAgentTypes.PurchasingAgent,

      priceModeLockedInOrangeMode: () =>
        user?.price_mode === PriceModes.ORANGE && user?.is_price_mode_lock,

      userCanShowMemoInOrangeMode: () =>
        user?.feature_settings?.show_memo_in_orange_mode,

      isPAUserSalesPerson: () =>
        user?.bmh_request_type ===
        PurchasingAgentTypes.PurchasingAgentSalesPerson,

      isPAUserOrPASalesPerson: () =>
        user?.bmh_request_type ===
          PurchasingAgentTypes.PurchasingAgentSalesPerson ||
        user?.bmh_request_type === PurchasingAgentTypes.PurchasingAgent,

      isOrangePriceMode: () => user?.price_mode === PriceModes.ORANGE,

      showPrivateHomePage: () =>
        !user?.feature_settings?.disable_company_level_internal_landing_page,

      showLabGrownPopup: () => user?.feature_settings?.lab_grown_pop_up,

      removeGreenModeForCompany: () =>
        user?.feature_settings?.remove_green_mode_for_company,

      removeUserLevelMarkUpBox: () =>
        user?.feature_settings?.remove_user_level_markup_box,

      userCanSetMarkupInOrangeMode: () =>
        !!user?.feature_settings
          ?.users_can_set_markup_when_locked_in_orange_mode,

      isPriceModeLock: () => !!user?.is_price_mode_lock,

      priceProfileVisibility: () => !!user?.price_profile_visibility,

      forceGreenPriceProfile: () => !!user?.force_green_price_profile,

      hideNotificationsOrangeMode: () =>
        !!user?.feature_settings?.hide_notifications_orange_mode,

      hideStockItemRequestNotification: () =>
        !!user?.feature_settings?.hide_stock_item_request_notification,

      roundUpPrices: () => !!user?.feature_settings?.round_up_prices,

      currencyConversion: () => user?.feature_settings?.currency_conversion,

      whiteLabellingOnWeb: () => user?.feature_settings?.white_labelling_on_web,

      allowSearchCoachOverlay: () => user?.allow_search_coach_overlay,

      isBusinessPlan: () => user?.has_custom_menu && user?.custom_theme,

      isProPlan: () => !user?.has_custom_menu && user?.custom_theme,

      isVdbMarketPlace: () => !user?.custom_theme,

      showSubTitleInGridView: () =>
        user?.feature_settings?.show_subtitle_on_grid_view,

      showBulkInOrangeMode: () =>
        user?.feature_settings?.orange_mode_bulk_request === true,

      enabledQuote: () => user?.feature_settings?.enable_quote,

      twoLineSubTitleInGridViewSearchResult: () =>
        user?.feature_settings?.two_line_sub_title_in_grid_view_search_result,

      itemDescriptionWebForJewelry: () =>
        user?.feature_settings?.item_description_web_for_jewelry,

      enableOrderCart: () => user?.feature_settings?.enable_order_cart,

      enableShoppingCart: () => user?.feature_settings?.shopping_cart,

      isAdmin: () => user?.is_admin_true,

      hasNoPlan: () => !user?.is_paid_plan_purchased,

      companyIdentifierEnabled: () =>
        user?.feature_settings?.company_identifier_enabled,
      enableSellReturnButton: () =>
        user?.user_permissions?.enable_sell_return_button,
      enableThirdPartyAddToCart: () =>
        user?.iframe_status &&
        user?.feature_settings?.checkout_method === "external_xml_cart",
      hasShopifyCheckout: () =>
        user?.feature_settings?.checkout_method === "shopify" &&
        user?.iframe_status,
      isSAASPlan: () => user?.feature_settings?.display_saas_plan,
    }),
    [user]
  );

  // Memoized plan-related checks
  const planChecks = useMemo(
    () => ({
      isActiveFreeTrial: () =>
        user?.plan_details?.plan?.is_trial &&
        user?.plan_details?.plan?.is_active,

      getUpcomingSubscription: () =>
        user?.plan_details?.upcoming_subscription ??
        user?.plan_details?.upcoming_renewal,

      isUpcomingPaidPlan: () =>
        (
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        )?.is_upcoming &&
        (
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        )?.is_paid &&
        !(
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        )?.is_active,

      isUpcomingNotPaidTrialPlan: () =>
        user?.plan_details?.plan?.is_trial &&
        (
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        )?.is_upcoming &&
        !(
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        )?.is_paid &&
        !(
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        )?.is_active,

      isUpcomingNotPaidPlan: () =>
        (
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        )?.is_upcoming &&
        !(
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        )?.is_paid &&
        !(
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        )?.is_active,

      isPaidPlan: () =>
        user?.plan_details?.plan?.is_active &&
        user?.plan_details?.plan?.is_paid &&
        (user?.plan_details?.plan?.plan_type === "Paid" ||
          user?.plan_details?.plan?.plan_type === "saas_plan"),
      isEnrolledTrial: () =>
        user?.plan_details?.plan?.is_trial &&
        user?.plan_details?.plan?.is_active &&
        user?.feature_settings?.display_saas_plan,
      planDetails: () => user?.plan_details,
      planExpiryDetails: () => user?.plan_details?.current_plan_expiry,
      isMembershipExpired: () =>
        !user?.plan_details?.plan?.is_active &&
        !user?.plan_details?.plan?.is_trial,
      isInExpiryBucketButNoUpcomingSubscription: () =>
        user?.plan_details?.plan?.is_active &&
        user?.plan_details?.current_plan_expiry?.bucket &&
        !(
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        ),
      isInExpiryBucketButUpcomingSubscription: () =>
        user?.plan_details?.plan?.is_active &&
        user?.plan_details?.current_plan_expiry?.bucket &&
        !!(
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        ) &&
        !(
          user?.plan_details?.upcoming_subscription ??
          user?.plan_details?.upcoming_renewal
        )?.is_paid,
    }),
    [user?.plan_details, user?.feature_settings]
  );

  // Memoized functions that take parameters - use useCallback
  const isMemoInOrangeModeOrPAUser = useCallback(
    (price_mode: number) =>
      price_mode === PriceModes.ORANGE &&
      (user?.feature_settings?.show_memo_in_orange_mode ||
        user?.bmh_request_type === PurchasingAgentTypes.PurchasingAgent),
    [user?.feature_settings?.show_memo_in_orange_mode, user?.bmh_request_type]
  );

  const onHandText = useCallback(
    (status: number) => user?.feature_settings?.on_hand_text?.[status],
    [user?.feature_settings?.on_hand_text]
  );

  const hasCustomSorting = useCallback(
    (productType: string) => {
      const customSorting = user?.feature_settings?.custom_sorting;
      if (customSorting && typeof customSorting[productType] === "boolean") {
        return customSorting[productType];
      }
      return false;
    },
    [user?.feature_settings?.custom_sorting]
  );

  const getAllowedBulkRequestTypes = useCallback(
    (allowedBulkRequestTypes: any) => {
      if (!allowedBulkRequestTypes) return [];
      return [...allowedBulkRequestTypes];
    },
    []
  );

  const getBulkRequestIconVisibility = useCallback(
    (productType?: ProductCategory) => {
      if (user?.feature_settings) {
        const allowedBulkRequestTypes =
          user?.feature_settings?.allowed_bulk_request_types;
        if (allowedBulkRequestTypes && allowedBulkRequestTypes.length > 0) {
          const requestTypes = getAllowedBulkRequestTypes(
            allowedBulkRequestTypes
          );
          if (productType) return requestTypes.includes(productType);
          else return requestTypes.length > 0;
        }
      }
      return false;
    },
    [user?.feature_settings, getAllowedBulkRequestTypes]
  );

  const getShoppingCartButtonVisibility = useCallback(
    () =>
      (user?.feature_settings?.enable_order_cart ||
        user?.feature_settings?.shopping_cart) &&
      user?.price_mode !== PriceModes.GREEN,
    [user?.feature_settings, user?.price_mode]
  );

  const showBulkRequestFeature = useCallback(
    (productType: ProductCategory | null, priceMode: number) => {
      switch (priceMode) {
        case PriceModes.WHITE:
          return getBulkRequestIconVisibility(productType ?? undefined);
        case PriceModes.ORANGE:
          return (
            booleanChecks.showBulkInOrangeMode() &&
            getBulkRequestIconVisibility(productType ?? undefined)
          );
        default:
          return false;
      }
    },
    [getBulkRequestIconVisibility, booleanChecks]
  );

  // Memoized onBoardingStatus that depends on theme
  const onBoardingStatus = useCallback(
    () =>
      theme.isLargeScreen
        ? user?.onboarding_status_on_web
        : theme.isMobile
        ? user?.onboarding_status
        : user?.onboarding_status_on_ipad,
    [
      theme.isLargeScreen,
      theme.isMobile,
      user?.onboarding_status_on_web,
      user?.onboarding_status,
      user?.onboarding_status_on_ipad,
    ]
  );

  // Memoized shouldShowAccountProfilePopUp
  const shouldShowAccountProfilePopUp = useCallback(() => {
    const { isAdmin, isActiveFreeTrial, isUpcomingPaidPlan, isPaidPlan } = {
      ...booleanChecks,
      ...planChecks,
    };

    if (
      (!isAdmin() && isActiveFreeTrial()) ||
      (!isAdmin() && !isActiveFreeTrial()) ||
      (isAdmin() && isUpcomingPaidPlan()) ||
      (isAdmin() && isPaidPlan()) ||
      !user?.plan_details?.plan?.is_trial
    )
      return false;

    const { visible, currentTimeStamp } = popUpVisibilty || {
      visible: true,
      currentTimeStamp: null,
    };

    if (visible) return true;
    if (currentTimeStamp) {
      const now = Date.now();
      const twentyFourHrs = 24 * 60 * 60 * 1000;
      return now - currentTimeStamp >= twentyFourHrs;
    }
    return false;
  }, [booleanChecks, planChecks, popUpVisibilty]);

  const allowSpaceInGlobalTextSearchFor20 = () => {
    return user?.feature_settings?.allow_space_in_global_text_search_for_2_0;
  };

  const disableImageZoom = () => {
    return user?.feature_settings?.disable_image_zoom;
  };

  const defaultStockItemType = () => {
    return user?.default_stock_item_type;
  };

  const restrictActionOnUnavailableItems = () => {
    return user?.feature_settings?.restrict_action_on_unavailable_items;
  };

  const hideLabGrown = () => {
    return !user?.allowed_stock_items_type?.includes("lab_grown_jewelry");
  };

  const hideJewelryBuilder = () => {
    return !user?.allowed_stock_items_type?.includes("jewelry_builder");
  };

  const hideDiamond = () => {
    return !user?.feature_settings?.enable_natural_jewelry_filter;
  };

  const checkAvailabilityStatusWithSupplier = () => {
    return user?.feature_settings?.check_availability_status_with_supplier;
  };

  return {
    user,
    isGuest,
    lastAdSequesnce,
    hideCustomLogo,
    customLogoURL,
    popUpVisibilty,
    // Spread memoized objects
    ...getters,
    ...booleanChecks,
    ...planChecks,
    // Callback functions
    isMemoInOrangeModeOrPAUser,
    onHandText,
    hasCustomSorting,
    getAllowedBulkRequestTypes,
    getBulkRequestIconVisibility,
    getShoppingCartButtonVisibility,
    showBulkRequestFeature,
    onBoardingStatus,
    shouldShowAccountProfilePopUp,
    restrictActionOnUnavailableItems,
    hideLabGrown,
    hideDiamond,
    hideJewelryBuilder,
    allowSpaceInGlobalTextSearchFor20,
    disableImageZoom,
    defaultStockItemType,
    checkAvailabilityStatusWithSupplier,
  };
};

export default userInfoSlice.reducer;
